import { IconName } from '@/components/ui/icon';
import type { Permission } from '@/lib/auth/permissions';
import { FeatureFlag } from '@/lib/feature-flags';

export type NavItem = {
  key: string;
  labelKey: string;
  href: string;
  icon: IconName;
  permissions?: Permission[];
  featureFlag?: FeatureFlag;
  comingSoon?: boolean;
  children?: NavItem[];
  /** Open link in a new browser tab */
  openInNewTab?: boolean;
};

export const NAV_CONFIG: NavItem[] = [
  // Main navigation
  {
    key: 'me',
    labelKey: 'myProfile',
    href: 'profile', // Will be replaced with actual profile href
    icon: 'user',
    permissions: ['authenticated'],
  },
  {
    key: 'leaderboard',
    labelKey: 'leaderboard',
    href: 'ranking',
    icon: 'chart',
    // No permissions = public
  },
  {
    key: 'events',
    labelKey: 'events',
    href: 'event',
    icon: 'calendar',
    permissions: ['authenticated'],
  },
  {
    key: 'matches',
    labelKey: 'matches',
    href: 'match',
    icon: 'list',
    permissions: ['authenticated'],
  },
  {
    key: 'memberships',
    labelKey: 'memberships',
    href: 'memberships/mine',
    icon: 'layers',
    permissions: ['authenticated'],
  },
  {
    key: 'admin',
    labelKey: 'admin',
    href: 'admin',
    icon: 'cog',
    permissions: ['admin', 'location-admin', 'event-admin'],
    children: [
      {
        key: 'federation-management',
        labelKey: 'federationManagement',
        href: 'admin/federation-management',
        icon: 'building',
        permissions: ['admin'],
      },
      {
        key: 'user-management',
        labelKey: 'userManagement',
        href: 'admin/user-management',
        icon: 'users',
        permissions: ['admin'],
      },
      {
        key: 'location-management',
        labelKey: 'locationManagement',
        href: 'admin/location-management',
        icon: 'building',
        permissions: ['admin', 'location-admin'],
      },
      {
        key: 'event-management',
        labelKey: 'eventManagement',
        href: 'admin/event-management',
        icon: 'calendar',
        permissions: ['admin', 'location-admin', 'event-admin'],
      },
      {
        key: 'box-management',
        labelKey: 'boxManagement',
        href: 'admin/box-management',
        icon: 'calendar',
        permissions: ['admin', 'location-admin'],
      },
      {
        key: 'series-management',
        labelKey: 'seriesManagement',
        href: 'admin/series-management',
        icon: 'layers',
        permissions: ['admin'],
      },
      {
        key: 'rating-recalculation',
        labelKey: 'ratingRecalculation',
        href: 'admin/rating-recalculation',
        icon: 'calculator',
        permissions: ['admin'],
        featureFlag: FeatureFlag.SUPER_ADMIN,
      },
      {
        key: 'admin-logs',
        labelKey: 'adminLogs',
        href: 'admin/admin-logs',
        icon: 'file-text',
        permissions: ['admin'],
        featureFlag: FeatureFlag.SUPER_ADMIN,
      },
      {
        key: 'email-campaigns',
        labelKey: 'emailCampaigns',
        href: 'admin/email-campaigns',
        icon: 'envelope',
        permissions: ['admin'],
        featureFlag: FeatureFlag.SUPER_ADMIN,
      },
    ],
  },
];

/**
 * Get admin navigation items
 */
export function getAdminNavItems(): NavItem[] {
  const adminItem = NAV_CONFIG.find((item) => item.key === 'admin');
  return adminItem?.children || [];
}

/**
 * Get navigation item by key
 */
export function getNavItemByKey(key: string): NavItem | undefined {
  function findItem(items: NavItem[]): NavItem | undefined {
    for (const item of items) {
      if (item.key === key) {
        return item;
      }
      if (item.children) {
        const found = findItem(item.children);
        if (found) return found;
      }
    }
    return undefined;
  }

  return findItem(NAV_CONFIG);
}

/**
 * Get all navigation items that match given permissions
 */
export function getNavItemsByPermissions(permissions: Permission[]): NavItem[] {
  function filterItems(items: NavItem[]): NavItem[] {
    return items.filter((item) => {
      // Check if user has permission for this item
      if (item.permissions && !item.permissions.some((p) => permissions.includes(p))) {
        return false;
      }

      // Filter children recursively
      if (item.children) {
        item.children = filterItems(item.children);
      }

      return true;
    });
  }

  return filterItems([...NAV_CONFIG]);
}

type Federation = { id: string; name: string; slug: string | null };

function getMarketingUrl(): string {
  return process.env.NEXT_PUBLIC_SITE_URL || 'https://playpulse.io';
}

/**
 * Creates navigation items for user's federations, linking to the marketing site.
 */
export function createFederationNavItems(federations: Federation[], locale: string): NavItem[] {
  const marketingUrl = getMarketingUrl();
  return federations
    .filter((f) => f.slug)
    .map((federation) => ({
      key: `federationPage-${federation.id}`,
      labelKey: federation.name,
      href: `${marketingUrl}/${locale}/federation/${federation.slug}/`,
      icon: 'flag' as const,
      permissions: ['authenticated' as const],
      openInNewTab: true,
    }));
}
